<template>
  <modal-vue @close="close" :modalActive="modalStateProp">
    <template #header>
      <p class="font-bold text-start text-xl">Visibilidade para equipe médica</p>
    </template>

    <div v-if="healthUnit" class="mb-0 flex flex-col h-full overflow-hidden">
      <Spinner
        v-if="isLoading"
        color="primary"
        size="5rem"
        width="0.5rem"
        class="absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50"
      />

      <div class="flex-shrink-0" :class="{ 'opacity-50': isLoading }">
        <div class="bg-grey-lighten-4 rounded-lg flex items-center">
          <div class="text-left flex-1">
            <p class="font-bold">Escalas</p>
            <p class="font-medium text-grey-darken-3">{{ healthUnit.name }}</p>
          </div>

          <div class="text-right flex-1 mr-3">
            <p>Visualizar escala completa</p>
          </div>

          <DsSwitch v-model="fullScheduleVisibility" />
        </div>

        <div class="flex justify-end px-12 mb-2 mt-4">
          <div class="flex items-center gap-12">
            <span class="text-sm w-12 text-center">App</span>
            <span v-if="splitVisibilityEnabled" class="text-sm w-12 text-center">Painel</span>
            <span class="text-sm w-12 text-center">Compartilhar</span>
          </div>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto min-h-0" :class="{ 'opacity-50': isLoading }">
        <div
          v-for="schedule in schedules"
          :key="schedule.id"
          class="bg-grey-lighten-4 rounded-lg px-4 py-3 mt-3"
          style="background-color: #f0f0f4"
        >
          <div class="flex items-center">
            <div class="text-left flex-1">
              <p class="text-xs" :class="!schedule.isVisible ? 'text-[#9E9EB7]' : '#16161D'">
                Escala:
              </p>
              <p class="font-bold" :class="!schedule.isVisible ? 'text-[#9E9EB7]' : '#16161D'">
                {{ schedule.name }}
              </p>
            </div>

            <div class="flex items-center px-4 gap-12">
              <div class="w-12 flex justify-center">
                <DsSwitch
                  :model-value="!!schedule.isVisible"
                  @update:model-value="(val: boolean) => (schedule.isVisible = val)"
                />
              </div>

              <div v-if="splitVisibilityEnabled" class="w-12 flex justify-center">
                <DsSwitch
                  :model-value="!!schedule.isVisibleWeb"
                  @update:model-value="(val: boolean) => (schedule.isVisibleWeb = val)"
                />
              </div>

              <div class="w-12 flex justify-center">
                <DsSwitch
                  :model-value="!!schedule.isShareable"
                  @update:model-value="(val: boolean) => (schedule.isShareable = val)"
                  :disabled="!schedule.isVisible"
                />
              </div>
            </div>
          </div>

          <div
            v-if="splitVisibilityEnabled && schedule.isVisible"
            class="mt-3 pt-3 border-t border-gray-300"
          >
            <div class="date-range-section gap-4">
              <p class="date-range-title text-[#2c3e50] text-left">
                Período de visibilidade para médicos
              </p>
              <VDatePicker
                :model-value="getDateRange(schedule)"
                @update:model-value="(val: DateRange) => updateDateRange(schedule, val)"
                is-range
                locale="pt-BR"
                :first-day-of-week="2"
              >
                <template #default="{ togglePopover }">
                  <div class="date-range-row">
                    <span class="date-range-label">De</span>
                    <button type="button" class="date-range-btn" @click="togglePopover">
                      <span :class="{ 'text-gray-400': !schedule.doctorVisibleFrom }">
                        {{ formatDateDisplay(schedule.doctorVisibleFrom) || 'Selecionar' }}
                      </span>
                      <svg
                        class="date-range-icon"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                    <span class="date-range-label">Até</span>
                    <button type="button" class="date-range-btn" @click="togglePopover">
                      <span :class="{ 'text-gray-400': !schedule.doctorVisibleUntil }">
                        {{ formatDateDisplay(schedule.doctorVisibleUntil) || 'Selecionar' }}
                      </span>
                      <svg
                        class="date-range-icon"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                    <button
                      v-if="schedule.doctorVisibleFrom || schedule.doctorVisibleUntil"
                      type="button"
                      class="date-range-clear-btn"
                      title="Limpar datas"
                      @click.stop="clearDoctorDates(schedule)"
                    >
                      <v-icon :icon="mdiCloseCircleOutline" size="22" color="#9E9EB7" />
                    </button>
                  </div>
                </template>
              </VDatePicker>
            </div>
          </div>
        </div>
        <div
          v-if="!isLoading && schedules.length === 0"
          class="bg-grey-lighten-4 rounded-lg px-4 py-4 mt-2 flex items-center justify-center text-grey-darken-1"
          style="background-color: #f0f0f4"
        >
          <p>Nenhuma escala encontrada para esta unidade.</p>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end mt-0">
        <outlined-button @click="close" text="Fechar" width="100px" />
      </div>
    </template>
  </modal-vue>
</template>

<script setup lang="ts">
import { ref, watch, toRefs, watchEffect, nextTick } from 'vue'
import DsSwitch from '@/components/designSystem/Switch/index.vue'
import { SimpleHealthUnitResponse } from '@/Network/Types/Responses/HealthUnitResponses'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import ScheduleService from '@/Network/Services/ScheduleUnitService'
import Spinner from '@/components/others/Spinner.vue'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { mdiCloseCircleOutline } from '@mdi/js'
import { useErrorToast } from '@/composables/useErrorToast'

const props = defineProps<{
  modalStateProp: boolean
  healthUnit?: SimpleHealthUnitResponse
}>()

const { flags } = useFeatureFlags()
const splitVisibilityEnabled = flags.enabledSplitScheduleVisibility

// Alias usado também no template: o `;` de um type literal inline dentro do atributo
// `@update:model-value` quebrava o parser do vue-tsc (TS1005).
type DateRange = { start: Date; end: Date }

interface Schedule {
  id: number
  name: string
  isVisible?: boolean
  isVisibleWeb?: boolean
  isShareable?: boolean
  doctorVisibleFrom?: string | null
  doctorVisibleUntil?: string | null
}

const scheduleService = new ScheduleService()
const { notifyError } = useErrorToast()
const schedules = ref<Schedule[]>([])
const originalSchedules = ref<Schedule[]>([])
const isLoading = ref(false)
const isUpdatingFromFetch = ref(false)

const isFullyVisible = (schedule: Schedule) =>
  splitVisibilityEnabled ? !!schedule.isVisible && !!schedule.isVisibleWeb : !!schedule.isVisible

const isFullyHidden = (schedule: Schedule) =>
  splitVisibilityEnabled ? !schedule.isVisible && !schedule.isVisibleWeb : !schedule.isVisible

async function fetchSchedules() {
  if (!props.healthUnit) return

  isLoading.value = true
  schedules.value = []
  originalSchedules.value = []
  try {
    const response = await scheduleService.list({
      healthUnitIds: [props.healthUnit.id]
    })

    const fetchedData = response.data.map((schedule) => {
      if (schedule.isVisible === false && schedule.isShareable === true) {
        return {
          ...schedule,
          isShareable: false
        }
      }
      return schedule
    })

    schedules.value = fetchedData
    originalSchedules.value = JSON.parse(JSON.stringify(response.data))

    isUpdatingFromFetch.value = true
    fullScheduleVisibility.value = response.data.every(isFullyVisible)

    await nextTick()
    isUpdatingFromFetch.value = false
  } catch (error) {
    // Sem o aviso o modal abria vazio, como se a unidade não tivesse escalas.
    notifyError(error, 'Não foi possível carregar as escalas da unidade.')
  } finally {
    isLoading.value = false
  }
}

const emit = defineEmits(['close'])
const { modalStateProp } = toRefs(props)

const close = () => {
  saveChanges()
  emit('close')
}

const fullScheduleVisibility = ref(true)

watch(fullScheduleVisibility, (newValue) => {
  if (isUpdatingFromFetch.value) return

  schedules.value = schedules.value.map((escala) => ({
    ...escala,
    isVisible: newValue,
    ...(splitVisibilityEnabled ? { isVisibleWeb: newValue } : {})
  }))
})

watch(
  schedules,
  (newSchedules) => {
    if (isUpdatingFromFetch.value) return

    if (newSchedules.every(isFullyVisible)) {
      fullScheduleVisibility.value = true
    } else if (newSchedules.every(isFullyHidden)) {
      fullScheduleVisibility.value = false
    }

    newSchedules.forEach((schedule) => {
      if (schedule.isVisible === false && schedule.isShareable === true) {
        schedule.isShareable = false
      }
    })
  },
  { deep: true }
)

watchEffect(() => {
  if (props.modalStateProp) {
    fetchSchedules()
  } else {
    schedules.value = []
    originalSchedules.value = []
  }
})

function formatDateDisplay(value: string | null | undefined): string {
  if (!value) return ''
  const [year, month, day] = value.split('-')
  return `${day}/${month}/${year}`
}

function toDateString(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function getDateRange(schedule: Schedule): { start: Date | null; end: Date | null } {
  return {
    start: parseDate(schedule.doctorVisibleFrom),
    end: parseDate(schedule.doctorVisibleUntil)
  }
}

function updateDateRange(schedule: Schedule, val: DateRange | null) {
  if (!val) {
    schedule.doctorVisibleFrom = null
    schedule.doctorVisibleUntil = null
    return
  }
  schedule.doctorVisibleFrom = toDateString(val.start)
  schedule.doctorVisibleUntil = toDateString(val.end)
}

function clearDoctorDates(schedule: Schedule) {
  schedule.doctorVisibleFrom = null
  schedule.doctorVisibleUntil = null
}

const VISIBILITY_KEYS: (keyof Schedule)[] = ['isVisible', 'isShareable']
const SPLIT_VISIBILITY_KEYS: (keyof Schedule)[] = [
  'isVisibleWeb',
  'doctorVisibleFrom',
  'doctorVisibleUntil'
]

function hasScheduleChanged(schedule: Schedule, original: Schedule): boolean {
  const keys = splitVisibilityEnabled
    ? [...VISIBILITY_KEYS, ...SPLIT_VISIBILITY_KEYS]
    : VISIBILITY_KEYS
  return keys.some((k) => (schedule[k] ?? null) !== (original[k] ?? null))
}

function buildUpdatePayload(schedule: Schedule) {
  const payload: Record<string, unknown> = {
    isVisible: schedule.isVisible,
    isShareable: schedule.isShareable
  }
  if (splitVisibilityEnabled) {
    payload.isVisibleWeb = schedule.isVisibleWeb
    payload.doctorVisibleFrom = schedule.doctorVisibleFrom ?? null
    payload.doctorVisibleUntil = schedule.doctorVisibleUntil ?? null
  }
  return payload
}

async function saveChanges() {
  const promises: Promise<any>[] = []

  schedules.value.forEach((schedule) => {
    const original = originalSchedules.value.find((o) => o.id === schedule.id)

    if (original && hasScheduleChanged(schedule, original)) {
      promises.push(scheduleService.update(schedule.id, buildUpdatePayload(schedule)))
    }
  })

  if (promises.length > 0) {
    try {
      await Promise.all(promises)
      originalSchedules.value = JSON.parse(JSON.stringify(schedules.value))
    } catch (error) {
      // O modal fecha logo após o save; o toast é o único aviso de que a
      // visibilidade não foi salva (e preserva a mensagem do backend).
      notifyError(error, 'Não foi possível salvar a visibilidade das escalas.')
    }
  }
}
</script>

<style scoped>
.date-range-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.date-range-title {
  font-size: 12px;
  color: #6b7280;
}

.date-range-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.date-range-label {
  font-size: 13px;
  color: #6b7280;
  flex-shrink: 0;
}

.date-range-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  background: white;
  cursor: pointer;
  white-space: nowrap;
}

.date-range-btn:hover {
  border-color: #9ca3af;
}

.date-range-icon {
  width: 16px;
  height: 16px;
  color: #9ca3af;
  flex-shrink: 0;
}

.date-range-clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.15s;
}

.date-range-clear-btn:hover {
  background-color: #fee2e2;
}

.date-range-clear-btn:hover :deep(.v-icon) {
  color: #ef4444 !important;
}
</style>

<style>
.vc-popover-content-wrapper {
  z-index: 9999 !important;
}
</style>
